import React, { useMemo } from 'react'
import { FaBoxes } from 'react-icons/fa'
import { STATUS } from '../lib/viz'
import { number } from '../lib/format'

/**
 * Enter stock as a size × colour grid, on the product form.
 *
 * This replaces a "Create stock lines for each size" button that sat in the
 * sidebar and did nothing visible. Adding a product and then hunting for a
 * second action to say how many you have is not how anyone thinks about it —
 * the quantity is part of adding the product.
 *
 * Laid out as a real grid rather than a flat list because that is how the stock
 * physically is: sizes down, colours across. A 9-size, 4-colour shirt is 36
 * numbers, and as a flat list of "Large · Navy" rows it is unreadable.
 *
 * Empty is not zero. A blank cell means "this combination is not stocked" and no
 * row is written for it; 0 means "stocked, currently none left" and does get a
 * row, so it shows on the restock list. Conflating the two would either bury the
 * list in combinations that never existed, or silently hide genuine sell-outs.
 */
const StockGrid = ({ sizes = [], colors = [], values, onChange, disabled = false }) => {
  // Always at least one row and column, so a product with no size or colour
  // options still gets a single quantity box rather than an empty grid.
  const rows = sizes.length ? sizes : ['']
  const cols = colors.length ? colors : ['']

  const key = (size, color) => `${size}|||${color}`

  const total = useMemo(
    () =>
      Object.values(values ?? {}).reduce((sum, v) => {
        const n = Number(v)
        return sum + (Number.isFinite(n) ? n : 0)
      }, 0),
    [values],
  )

  const trackedCells = useMemo(
    () => Object.values(values ?? {}).filter((v) => v !== '' && v !== null && v !== undefined).length,
    [values],
  )

  const set = (size, color, raw) => {
    const next = { ...(values ?? {}) }
    if (raw === '') {
      delete next[key(size, color)]
    } else {
      // Clamp rather than reject: a negative stock level is not a state the
      // shop can be in, and the database check constraint would refuse it
      // anyway — better to correct it here than fail the save.
      next[key(size, color)] = Math.max(0, Number.parseInt(raw, 10) || 0)
    }
    onChange(next)
  }

  /** Fill every empty cell in a row, for the common "12 of each colour" case. */
  const fillRow = (size, raw) => {
    const value = raw === '' ? '' : Math.max(0, Number.parseInt(raw, 10) || 0)
    const next = { ...(values ?? {}) }
    for (const color of cols) {
      if (value === '') delete next[key(size, color)]
      else next[key(size, color)] = value
    }
    onChange(next)
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-2xs text-ink-muted">
          Leave a box empty if you never stock that combination. Enter <strong>0</strong> if you
          stock it but have none right now — that puts it on the restock list.
        </p>
        {trackedCells > 0 && (
          <span className="shrink-0 text-2xs font-medium text-ink-secondary">
            {number(total)} units across {trackedCells} line{trackedCells === 1 ? '' : 's'}
          </span>
        )}
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th className="min-w-[70px]">{sizes.length ? 'Size' : ''}</th>
              {cols.map((color) => (
                <th key={color || 'single'} className="num min-w-[74px]">
                  {color || 'Quantity'}
                </th>
              ))}
              {cols.length > 1 && <th className="num min-w-[70px]">Set all</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((size) => (
              <tr key={size || 'single'}>
                <td className="font-medium">{size || '—'}</td>

                {cols.map((color) => {
                  const value = values?.[key(size, color)]
                  const isZero = value === 0
                  return (
                    <td key={color || 'single'} className="num">
                      <input
                        type="number"
                        min="0"
                        disabled={disabled}
                        value={value ?? ''}
                        onChange={(e) => set(size, color, e.target.value)}
                        placeholder="–"
                        aria-label={`Stock for ${size || 'this product'}${color ? ` in ${color}` : ''}`}
                        className="input input-sm w-16 text-right"
                        // A zero is the one number that needs to stand out: it
                        // is the difference between "sold out" and "not stocked",
                        // and they sit in identical-looking boxes otherwise.
                        style={isZero ? { color: STATUS.critical, fontWeight: 600 } : undefined}
                      />
                    </td>
                  )
                })}

                {cols.length > 1 && (
                  <td className="num">
                    <input
                      type="number"
                      min="0"
                      disabled={disabled}
                      placeholder="all"
                      aria-label={`Set every colour of ${size} at once`}
                      onChange={(e) => fillRow(size, e.target.value)}
                      className="input input-sm w-14 text-right text-ink-muted"
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {trackedCells === 0 && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-hairline bg-plane/60 px-3 py-2.5">
          <FaBoxes className="mt-0.5 shrink-0 text-[11px] text-ink-muted" aria-hidden="true" />
          <p className="text-2xs text-ink-secondary">
            No stock tracked yet, so this product always shows as available on the shop. Enter
            quantities above to start tracking it — it will then appear on the Inventory page and go
            out of stock by itself when it runs out.
          </p>
        </div>
      )}
    </div>
  )
}

export default StockGrid
export const stockKey = (size, color) => `${size}|||${color}`

/** Turn the grid's flat map back into rows the database understands. */
export function gridToRows(values) {
  return Object.entries(values ?? {})
    .filter(([, v]) => v !== '' && v !== null && v !== undefined)
    .map(([k, quantity]) => {
      const [size, color] = k.split('|||')
      return { size, color, quantity: Number(quantity) || 0 }
    })
}

/** And the inventory rows back into the grid's map. */
export function rowsToGrid(rows) {
  const out = {}
  for (const row of rows ?? []) {
    out[stockKey(row.size ?? '', row.color ?? '')] = row.quantity ?? 0
  }
  return out
}
